import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { usePermit } from '../context/PermitContext';
import { useGuru } from '../context/GuruContext';
import { useUserAccess } from '../context/UserAccessContext';
import { ShieldAlert, LogOut, GraduationCap } from 'lucide-react';
import { PageTransition } from './transitions/PageTransition';
import { LoadingSpinner } from './LoadingSpinner';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';


export const InactiveAccountScreen: React.FC = () => {
  const navigate = useNavigate();
  const { authUser, logout } = useAuth();
  const { loading } = usePermit();
  const { allGuruRecords } = useGuru();
  const { isAkunLayanan } = useUserAccess();

  const [checkingChildren, setCheckingChildren] = useState<boolean>(true);
  const [hasChildren, setHasChildren] = useState<boolean>(false);

  const currentGuru = useMemo(() => allGuruRecords.find(g => g.email === authUser?.email), [allGuruRecords, authUser]);

  useEffect(() => {
    const checkChildren = async () => {
      if (loading) return;

      if (!currentGuru) {
        setCheckingChildren(false);
        return;
      }

      if (currentGuru.noKk) {
        try {
          const q = query(collection(db, 'santri'), where('noKk', '==', currentGuru.noKk));
          const snapshot = await getDocs(q);
          setHasChildren(!snapshot.empty);
        } catch (error) {
          console.error("Error checking santri children:", error);
          setHasChildren(false);
        }
      } else {
        setHasChildren(false);
      }
      setCheckingChildren(false);
    };

    checkChildren();
  }, [currentGuru, loading]);

  if (loading || checkingChildren) {
    return <LoadingSpinner />;
  }

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden p-8 text-center">
          <div className="mx-auto w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
            <ShieldAlert className="w-10 h-10 text-red-600" />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-4">Akses Diblokir</h1>

          <p className="text-gray-600 mb-8 leading-relaxed">
            {isAkunLayanan
              ? <>Akun layanan ini sedang <span className="font-semibold text-red-600">dinonaktifkan</span> oleh admin. Hubungi admin untuk mengaktifkannya kembali.</>
              : <>Mohon maaf, akun Anda saat ini berstatus <span className="font-semibold text-red-600">Nonaktif</span> sehingga akses ke Portal Internal MIAS telah ditutup.</>}
          </p>

          <div className="space-y-4">
            {hasChildren && (
              <button
                onClick={() => navigate('/portal-wali')}
                className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors duration-200"
              >
                <GraduationCap className="w-5 h-5" />
                Lanjut ke Portal Wali Santri
              </button>
            )}

            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-6 rounded-xl border border-gray-200 transition-colors duration-200"
            >
              <LogOut className="w-5 h-5" />
              Keluar
            </button>
          </div>
        </div>
      </div>
    </PageTransition>
  );
};
